"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../../components/ui/dialog"
import { Button } from "../../../../components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../components/ui/tabs"
import { Label } from "../../../../components/ui/label"
import { Input } from "../../../../components/ui/input"
import { Textarea } from "../../../../components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select"
import { Switch } from "../../../../components/ui/switch"
import { FileText } from "lucide-react"

const DocumentNodeModal = ({ isOpen, onClose, nodeData, onSave }) => {
  const [formData, setFormData] = useState({})
  const [activeTab, setActiveTab] = useState("general")

  // Inicializar formData cuando se abre el modal o cambia el nodo
  useEffect(() => {
    if (isOpen && nodeData) {
      setFormData({
        label: nodeData.label || "",
        description: nodeData.description || "",
        ...nodeData.config,
      })
    }
  }, [isOpen, nodeData])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSwitchChange = (name, checked) => {
    setFormData((prev) => ({ ...prev, [name]: checked }))
  }

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSave = () => {
    // Separar los datos básicos de los de configuración
    const { label, description, ...config } = formData
    onSave({ label, description, config })
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <FileText className="h-5 w-5 text-blue-600 mr-2" />
            Configuración de Documento
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-3 mb-4">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="specific">Específico</TabsTrigger>
            <TabsTrigger value="advanced">Avanzado</TabsTrigger>
          </TabsList>

          {/* Pestaña General */}
          <TabsContent value="general" className="space-y-4">
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="label">Nombre</Label>
                <Input id="label" name="label" value={formData.label || ""} onChange={handleInputChange} />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description || ""}
                  onChange={handleInputChange}
                  rows={3}
                />
              </div>
            </div>
          </TabsContent>

          {/* Pestaña Específica para Documentos */}
          <TabsContent value="specific" className="space-y-4">
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="docType">Tipo de Documento</Label>
                <Select
                  value={formData.docType || "pdf"}
                  onValueChange={(value) => handleSelectChange("docType", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="word">Word</SelectItem>
                    <SelectItem value="excel">Excel</SelectItem>
                    <SelectItem value="image">Imagen</SelectItem>
                    <SelectItem value="text">Texto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="source">Fuente</Label>
                <Select
                  value={formData.source || "upload"}
                  onValueChange={(value) => handleSelectChange("source", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar fuente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="upload">Carga Manual</SelectItem>
                    <SelectItem value="email">Correo Electrónico</SelectItem>
                    <SelectItem value="scanner">Escáner</SelectItem>
                    <SelectItem value="api">API Externa</SelectItem>
                    <SelectItem value="ftp">FTP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="requiresValidation">Requiere Validación</Label>
                <Switch
                  id="requiresValidation"
                  checked={formData.requiresValidation || false}
                  onCheckedChange={(checked) => handleSwitchChange("requiresValidation", checked)}
                />
              </div>
            </div>
          </TabsContent>

          {/* Pestaña Avanzada */}
          <TabsContent value="advanced" className="space-y-4">
            <div className="grid gap-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="isRequired">Obligatorio</Label>
                <Switch
                  id="isRequired"
                  checked={formData.isRequired || false}
                  onCheckedChange={(checked) => handleSwitchChange("isRequired", checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="isVisible">Visible</Label>
                <Switch
                  id="isVisible"
                  checked={formData.isVisible !== false} // Por defecto true
                  onCheckedChange={(checked) => handleSwitchChange("isVisible", checked)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="customId">ID Personalizado</Label>
                <Input
                  id="customId"
                  name="customId"
                  value={formData.customId || ""}
                  onChange={handleInputChange}
                  placeholder="Dejar en blanco para ID automático"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="notes">Notas</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes || ""}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Notas adicionales sobre este nodo"
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>Guardar Cambios</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default DocumentNodeModal

